import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Building2,
  Check,
  Copy,
  LifeBuoy,
  Loader2,
  Mail,
  PauseCircle,
  PlayCircle,
  Plus,
  ShieldAlert,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { VERTICAL_OPTIONS, type PlatformTenantRow, type VerticalKey } from "@rn/contracts";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { relativeTime } from "@/lib/crm";

export const Route = createFileRoute("/_authenticated/plataforma")({
  component: PlataformaPage,
});

const ROTULO_VERTICAL: Record<string, string> = Object.fromEntries(
  VERTICAL_OPTIONS.map((v) => [v.value, v.label]),
);

const erroDe = (e: unknown, padrao: string) => (e instanceof ApiError ? e.message : padrao);

type Convite = {
  email: string | null;
  status: "linked" | "invited";
  inviteUrl: string | null;
};

/**
 * Painel da plataforma — só para os donos do produto.
 *
 * Quem vê é decidido pelo servidor (`PLATFORM_ADMIN_EMAILS`); esconder a tela
 * aqui é só educação: todas as rotas de `/platform` recusam com 403 quem não
 * está na lista, tenha o papel que tiver na empresa.
 */
function PlataformaPage() {
  const { user, enterSupport } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [criando, setCriando] = useState(false);
  const [suspendendo, setSuspendendo] = useState<PlatformTenantRow | null>(null);
  const [convidando, setConvidando] = useState<PlatformTenantRow | null>(null);
  const [convite, setConvite] = useState<Convite | null>(null);

  const empresas = useQuery({
    queryKey: ["platform", "tenants"],
    queryFn: () => api.get<PlatformTenantRow[]>("/platform/tenants"),
    enabled: Boolean(user?.isPlatformAdmin),
  });

  const atualizar = () => queryClient.invalidateQueries({ queryKey: ["platform", "tenants"] });

  const reativar = useMutation({
    mutationFn: (id: string) => api.post(`/platform/tenants/${id}/reactivate`),
    onSuccess: async () => {
      toast.success("Empresa reativada.");
      await atualizar();
    },
    onError: (e) => toast.error(erroDe(e, "Não foi possível reativar")),
  });

  const suporte = useMutation({
    mutationFn: (id: string) => enterSupport(id),
    onSuccess: () => void navigate({ to: "/painel" }),
    onError: (e) => toast.error(erroDe(e, "Não foi possível entrar para suporte")),
  });

  if (!user?.isPlatformAdmin) {
    return (
      <PageBody>
        <EmptyState
          icon={ShieldAlert}
          title="Área restrita"
          description="O painel da plataforma é só para os donos do produto."
        />
      </PageBody>
    );
  }

  return (
    <>
      <PageHeader
        title="Plataforma"
        description="Empresas clientes: criar, entrar para suporte e suspender"
        action={
          <Button onClick={() => setCriando(true)}>
            <Plus />
            Nova empresa para cliente
          </Button>
        }
      />
      <PageBody className="pb-10">
        {empresas.isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : (empresas.data ?? []).length === 0 ? (
          <EmptyState icon={Building2} title="Nenhuma empresa ainda" />
        ) : (
          <ul className="divide-y rounded-xl border bg-card">
            {(empresas.data ?? []).map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <div className="min-w-0 flex-1 basis-60">
                  <p className="flex items-center gap-2 truncate text-sm font-medium">
                    {t.name}
                    {t.active ? (
                      <Badge variant="success">Ativa</Badge>
                    ) : (
                      <Badge variant="danger">Suspensa</Badge>
                    )}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {ROTULO_VERTICAL[t.vertical] ?? t.vertical} · criada{" "}
                    {new Date(t.createdAt).toLocaleDateString("pt-BR")} · último uso{" "}
                    {relativeTime(t.lastActivityAt)}
                  </p>
                  {!t.active && t.suspendedReason && (
                    <p className="truncate text-xs text-destructive">Motivo: {t.suspendedReason}</p>
                  )}
                </div>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  <span>
                    <strong className="text-foreground">{t.users}</strong> usuário(s)
                  </span>
                  <span>
                    <strong className="text-foreground">{t.whatsappNumbers}</strong> WhatsApp
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={suporte.isPending}
                    onClick={() => suporte.mutate(t.id)}
                  >
                    <LifeBuoy />
                    Entrar para suporte
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConvidando(t)}>
                    <Mail />
                    Convidar dono
                  </Button>
                  {t.active ? (
                    <Button size="sm" variant="ghost" onClick={() => setSuspendendo(t)}>
                      <PauseCircle />
                      Suspender
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={reativar.isPending}
                      onClick={() => reativar.mutate(t.id)}
                    >
                      <PlayCircle />
                      Reativar
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </PageBody>

      <CriarEmpresa
        open={criando}
        onOpenChange={setCriando}
        onCriada={async (r) => {
          setConvite(r);
          await atualizar();
        }}
      />
      <SuspenderEmpresa
        empresa={suspendendo}
        onClose={() => setSuspendendo(null)}
        onFeito={atualizar}
      />
      <ConvidarDono
        empresa={convidando}
        onClose={() => setConvidando(null)}
        onFeito={(r) => setConvite(r)}
      />
      <ResultadoConvite convite={convite} onClose={() => setConvite(null)} />
    </>
  );
}

function CriarEmpresa({
  open,
  onOpenChange,
  onCriada,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCriada: (convite: Convite | null) => Promise<void>;
}) {
  const [nome, setNome] = useState("");
  const [vertical, setVertical] = useState<VerticalKey>("aesthetics");
  const [email, setEmail] = useState("");
  const [incluirMe, setIncluirMe] = useState(false);

  const criar = useMutation({
    mutationFn: () =>
      api.post<{ tenant: { name: string }; owner: Convite | null }>("/platform/tenants", {
        name: nome.trim(),
        vertical,
        ownerEmail: email.trim() || null,
        includeMe: incluirMe,
      }),
    onSuccess: async (r) => {
      toast.success(`Empresa "${r.tenant.name}" criada.`);
      onOpenChange(false);
      setNome("");
      setEmail("");
      setIncluirMe(false);
      await onCriada(r.owner);
    },
    onError: (e) => toast.error(erroDe(e, "Não foi possível criar a empresa")),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nova empresa para cliente</DialogTitle>
          <DialogDescription>
            Nasce com o funil, as etiquetas e as automações-modelo (desligadas) do vertical. O dono
            vira administrador: com conta, na hora; sem conta, por convite.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            criar.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="empresa-nome">Nome da empresa</Label>
            <Input id="empresa-nome" value={nome} onChange={(e) => setNome(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="empresa-vertical">Tipo de negócio</Label>
            <Select
              id="empresa-vertical"
              value={vertical}
              onChange={(e) => setVertical(e.target.value as VerticalKey)}
            >
              {VERTICAL_OPTIONS.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="empresa-dono">E-mail do dono</Label>
            <Input
              id="empresa-dono"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="dono@cliente.com.br"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={incluirMe}
              onChange={(e) => setIncluirMe(e.target.checked)}
            />
            Me incluir como administrador
          </label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={criar.isPending || nome.trim().length < 2 || (!email.trim() && !incluirMe)}
            >
              {criar.isPending && <Loader2 className="animate-spin" />}
              Criar empresa
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SuspenderEmpresa({
  empresa,
  onClose,
  onFeito,
}: {
  empresa: PlatformTenantRow | null;
  onClose: () => void;
  onFeito: () => Promise<unknown>;
}) {
  const [motivo, setMotivo] = useState("");
  const suspender = useMutation({
    mutationFn: () =>
      api.post(`/platform/tenants/${empresa!.id}/suspend`, { reason: motivo.trim() || null }),
    onSuccess: async () => {
      toast.success("Empresa suspensa.");
      setMotivo("");
      onClose();
      await onFeito();
    },
    onError: (e) => toast.error(erroDe(e, "Não foi possível suspender")),
  });

  return (
    <Dialog open={Boolean(empresa)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Suspender {empresa?.name}?</DialogTitle>
          <DialogDescription>
            Ninguém da empresa entra e nada automático sai (automações, acompanhamentos, lembretes,
            conversões). O que chega — mensagens e eventos do checkout — continua sendo gravado, mas
            não dispara nada, nem depois da reativação. Nada é apagado.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="motivo-suspensao">Motivo (fica na auditoria da empresa)</Label>
          <Textarea
            id="motivo-suspensao"
            rows={2}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: pagamento em atraso"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            disabled={suspender.isPending}
            onClick={() => suspender.mutate()}
          >
            Suspender
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConvidarDono({
  empresa,
  onClose,
  onFeito,
}: {
  empresa: PlatformTenantRow | null;
  onClose: () => void;
  onFeito: (convite: Convite) => void;
}) {
  const [email, setEmail] = useState("");
  const convidar = useMutation({
    mutationFn: () =>
      api.post<{ status: Convite["status"]; inviteUrl: string | null }>(
        `/platform/tenants/${empresa!.id}/owner-invite`,
        { email: email.trim() },
      ),
    onSuccess: (r) => {
      onFeito({ email: email.trim(), status: r.status, inviteUrl: r.inviteUrl });
      setEmail("");
      onClose();
    },
    onError: (e) => toast.error(erroDe(e, "Não foi possível convidar")),
  });

  return (
    <Dialog open={Boolean(empresa)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Convidar dono de {empresa?.name}</DialogTitle>
          <DialogDescription>
            Com conta, vira administrador na hora. Sem conta, recebe um link novo — o anterior deixa
            de valer.
          </DialogDescription>
        </DialogHeader>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="dono@cliente.com.br"
          aria-label="E-mail do dono"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={convidar.isPending || !email.trim()} onClick={() => convidar.mutate()}>
            Convidar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** O que aconteceu com o dono: vinculado na hora, ou o link do convite para copiar. */
function ResultadoConvite({ convite, onClose }: { convite: Convite | null; onClose: () => void }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <Dialog open={Boolean(convite)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {convite?.status === "linked" ? "Dono vinculado" : "Convite do dono"}
          </DialogTitle>
          <DialogDescription>
            {convite?.status === "linked"
              ? `${convite.email} já tinha conta e virou administrador da empresa.`
              : `O CRM não envia e-mail: mande este link para ${convite?.email}. Ele vale 7 dias e só pode ser usado uma vez.`}
          </DialogDescription>
        </DialogHeader>
        {convite?.inviteUrl && (
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 break-all rounded-md border bg-muted/40 px-2 py-1.5 font-mono text-xs">
              {convite.inviteUrl}
            </code>
            <Button
              size="icon-sm"
              variant="outline"
              aria-label="Copiar link do convite"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(convite.inviteUrl!);
                  setCopiado(true);
                  window.setTimeout(() => setCopiado(false), 2000);
                } catch {
                  toast.error("Não foi possível copiar — selecione e copie à mão");
                }
              }}
            >
              {copiado ? <Check className="text-success" /> : <Copy />}
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
